import React, { useMemo, useCallback } from 'react'

import moment from 'moment/min/moment-with-locales'
import { twMerge } from 'tailwind-merge'

import { type DateObject } from '@/types'
import { type OpeningHoursProps } from '@/types'
import EntityButtonsGroup, {
  RenderButton,
} from '@components/EntityButtonsGroup'
import Icon from '@components/Icon'
import OpeningDays from '@components/OpeningDays'
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react'
import { formatDateRange } from '@helpers/dateHelper'
import { getLocale } from '@helpers/translator'
import {
  trans,
  CHEF,
  STAR,
  MICHELIN,
  OPEN,
  CLOSED,
  CAPACITY,
  PERSONNES,
  TERRACE,
  ACCESSIBLE_PMR,
  NO_ACCESSIBLE_PMR,
  ECO_RESPONSABLE,
  SERVICES_ACCESSIBILITE,
  RESERVATION,
  RESERVATION_SITE,
  ADDRESS,
  EVENT_DATES,
  EVENT_START_TIME,
  ORGANIZER,
  CATEGORY,
  PLACE_TYPE,
  CURRENTLY,
  RESTAURANT_TYPE,
} from '@helpers/translator'
import getOpeningHoursStatus from '@hooks/useOpeningHours'
import { useTailwindBreakpoint } from '@hooks/useTailwindBreakpoint'

interface ServicesAccessibiliteProps {
  pmr: boolean
  terrace?: boolean
  ecoResponsable?: boolean
  capacity?: number | string
}

interface AddressProps {
  address?: string
  latitude?: number
  longitude?: number
}

interface EventDetailsProps {
  startDate?: string | DateObject
  endDate?: string | DateObject
  openingHours: string
  organizer: string
  category: string
  servicesAccessibilite?: ServicesAccessibiliteProps
  address?: AddressProps
  website?: string
  email?: string
  phone?: string
  restaurant?: {
    star?: number
    chef?: string | string[]
    type?: string
  }
  hotel?: {
    star?: number
    url?: string
  }
  type: string
  allOpeningHours: OpeningHoursProps
}

export const Row: React.FC<{
  label?: string
  value: string | React.ReactNode
  className?: string
  valueClassName?: string
}> = ({ label, value, className, valueClassName }) => {
  return (
    <div
      className={twMerge(
        'flex flex-col gap-3 prose pt-[28px] pb-6 px-[32px] border-b border-grey-lighter last:border-b-0',
        className,
      )}
    >
      {label && <span className="font-bold">{label}</span>}
      {typeof value === 'string' ? (
        <span className={twMerge('font-medium', valueClassName)}>{value}</span>
      ) : (
        value
      )}
    </div>
  )
}

const RenderServicesAccessibilite: React.FC<ServicesAccessibiliteProps> = ({
  capacity,
  terrace,
  pmr,
  ecoResponsable,
}) => {
  const item = useCallback((icon: string, label: string) => {
    return (
      <div className="flex items-center gap-2.5">
        <Icon name={icon} size="32" />
        <span className="font-medium">{label}</span>
      </div>
    )
  }, [])

  const content = useMemo(
    () => (
      <div className="grid grid-cols-2 gap-y-6 mt-[14px]">
        {capacity &&
          item(
            'capacity',
            `${trans(CAPACITY)} : ${capacity} ${trans(PERSONNES, { personnes: capacity })}`,
          )}
        {terrace && item('terrace', trans(TERRACE))}
        {item(
          pmr ? 'pmr' : 'no_pmr',
          pmr ? trans(ACCESSIBLE_PMR) : trans(NO_ACCESSIBLE_PMR),
        )}
        {ecoResponsable && item('leaf', trans(ECO_RESPONSABLE))}
      </div>
    ),
    [capacity, terrace, pmr, ecoResponsable, item],
  )

  return <Row label={trans(SERVICES_ACCESSIBILITE)} value={content} />
}

const RenderStars: React.FC<{
  star: number
  type: 'star' | 'star_michelin'
}> = ({ star, type }) => {
  if (!star || star < 1) return null

  const stars = useMemo(
    () =>
      Array.from({ length: star }, (_, index) => (
        <Icon name={type} size="16" key={index} />
      )),
    [star, type],
  )

  const content = useMemo(
    () => (
      <div className="flex flex-wrap items-center gap-x-2.5">
        {stars}{' '}
        <span className="font-medium">
          {star} {trans(STAR, { star: star })}{' '}
          {type === 'star_michelin' ? trans(MICHELIN) : ''}
        </span>
      </div>
    ),
    [stars, star, type],
  )

  return <Row value={content} />
}

const RenderUrl: React.FC<{ url: string }> = ({ url }) => {
  const content = useMemo(
    () => (
      <RenderButton href={url} target="_blank" iconName="website">
        {trans(RESERVATION)}
      </RenderButton>
    ),
    [url],
  )

  return (
    <Row
      label={trans(RESERVATION_SITE)}
      value={content}
      className="not-prose"
    />
  )
}

const RenderAddress: React.FC<{
  address: AddressProps
  className?: string
}> = ({ address, className }) => {
  if (!address) return null
  const displayAddress = address.address
  return displayAddress ? (
    <Row
      label={trans(ADDRESS)}
      value={displayAddress}
      className={twMerge('border-none pb-0 pt-0', className)}
    />
  ) : null
}

const RenderChefs: React.FC<{ chefs: string | string[] }> = ({ chefs }) => {
  const chefItem = useCallback((chef: string) => {
    return (
      <span className="flex items-center gap-2.5 font-medium" key={chef}>
        <Icon name="chef" size="16" /> {chef}
      </span>
    )
  }, [])

  const content = Array.isArray(chefs) ? (
    <div className="grid grid-cols-2 gap-2.5">
      {chefs.map((chef) => chefItem(chef))}
    </div>
  ) : (
    chefItem(chefs)
  )

  return (
    <Row
      label={trans(CHEF, { chefs: Array.isArray(chefs) ? chefs.length : 1 })}
      value={content}
    />
  )
}

const EventDetails: React.FC<EventDetailsProps> = (props) => {
  const hasValues = Object.values(props).some(
    (value) => value !== null && value !== undefined,
  )

  if (!hasValues) {
    return null
  }

  const {
    startDate,
    endDate,
    openingHours,
    organizer,
    category,
    servicesAccessibilite,
    address,
    website,
    email,
    phone,
    type,
    restaurant,
    hotel,
    allOpeningHours,
  } = props

  moment.locale(getLocale())

  const isOpen = getOpeningHoursStatus(allOpeningHours)
  const isMobile = useTailwindBreakpoint('xl-')

  const dateRange = useMemo(
    () => formatDateRange(startDate, endDate),
    [startDate, endDate],
  )

  const renderRestaurantDetails = useCallback(
    () =>
      restaurant &&
      (restaurant.star || restaurant.chef || restaurant.type) && (
        <>
          {restaurant.star ? (
            <RenderStars star={restaurant.star} type="star_michelin" />
          ) : null}
          {restaurant.chef ? <RenderChefs chefs={restaurant.chef} /> : null}
          {type ? <Row label={trans(PLACE_TYPE)} value={type} /> : null}
          {restaurant.type ? (
            <Row label={trans(RESTAURANT_TYPE)} value={restaurant.type} />
          ) : null}
        </>
      ),
    [restaurant],
  )

  const renderHotelDetails = useCallback(
    () =>
      hotel &&
      (hotel.star || hotel.url) && (
        <>
          {hotel.star ? <RenderStars star={hotel.star} type="star" /> : null}
          {type ? <Row label={trans(PLACE_TYPE)} value={type} /> : null}
          {hotel.url ? <RenderUrl url={hotel.url} /> : null}
        </>
      ),
    [hotel],
  )

  const isRestaurant =
    restaurant && (restaurant.star || restaurant.chef || restaurant.type)
  const isHotel = hotel && (hotel.star || hotel.url)

  return (
    <div className="flex flex-col xl:flex-row xl:gap-5">
      <div className="flex w-full xl:w-6/12 flex-col h-max gap-2 border border-grey-lighter">
        {(startDate || endDate) && (
          <Row
            label={trans(EVENT_DATES)}
            value={dateRange}
            valueClassName="first-letter:uppercase"
          />
        )}
        {openingHours && (
          <Row label={trans(EVENT_START_TIME)} value={openingHours} />
        )}
        {organizer && <Row label={trans(ORGANIZER)} value={organizer} />}
        {type && !isRestaurant && !isHotel && (
          <Row label={trans(PLACE_TYPE)} value={type} />
        )}
        {category && <Row label={trans(CATEGORY)} value={category} />}
        {renderRestaurantDetails()}
        {renderHotelDetails()}
        {servicesAccessibilite && (
          <RenderServicesAccessibilite {...servicesAccessibilite} />
        )}
      </div>
      {(address || website || email || phone) && !allOpeningHours && (
        <div className="flex w-full xl:w-5/12 order-first border-b-0 xl:order-last flex-col py-6 gap-6 h-max border xl:border-b border-grey-lighter">
          {address && <RenderAddress address={address} />}
          {(website || email || phone) && (
            <EntityButtonsGroup
              entity={{ website, email, phone }}
              className="px-[32px]"
            />
          )}
        </div>
      )}
      {allOpeningHours && (
        <Disclosure
          as={'div' as const}
          defaultOpen={!isMobile}
          className="flex w-full xl:w-5/12 grow order-first border-b-0 xl:order-last flex-col py-6 px-[32px] gap-6 h-max border xl:border-b border-grey-lighter"
        >
          <DisclosureButton className="flex items-center justify-between w-full">
            {({ open }: { open: boolean }) => (
              <>
                <div className="flex items-center gap-2 prose text-deep-black font-medium ltr:mr-2 rtl:ml-2 text-left">
                  <Icon name="clock" size="16" /> {trans(CURRENTLY)}
                  {isOpen ? (
                    <span className="text-green-500">{trans(OPEN)}</span>
                  ) : (
                    <span className="text-red-500">{trans(CLOSED)}</span>
                  )}
                </div>
                <div
                  className={`transform transition-transform duration-500 ease-out ${open ? 'rotate-180' : ''}`}
                >
                  <Icon name="chevron_down" size="13" />
                </div>
              </>
            )}
          </DisclosureButton>
          <DisclosurePanel
            transition
            className="origin-top transition duration-200 ease-out flex flex-col gap-y-6 data-[closed]:-translate-y-1 data-[closed]:opacity-0"
          >
            <OpeningDays allOpeningHours={allOpeningHours} />
            {address && <RenderAddress address={address} className="px-0" />}
          </DisclosurePanel>
        </Disclosure>
      )}
    </div>
  )
}

export default EventDetails
